import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiDocumentDuplicate } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import Button from '../../../../components/Button';
import { useDuplicateSession } from '../../../../hooks/useSessions';

interface SessionDuplicateSectionProps {
  sessionId: string;
}

export default function SessionDuplicateSection({ sessionId }: SessionDuplicateSectionProps): ReactElement {
  const navigate = useNavigate();
  const duplicateSessionMutation = useDuplicateSession();

  const duplicate = async (mode: 'without_runs' | 'with_runs') => {
    try {
      const result = await duplicateSessionMutation.mutateAsync({ sessionId, mode });
      // A multi-gate reference brings its comparison sessions along.
      const comparisons: number = result.comparisons ?? 0;
      const withComparisons = comparisons
        ? ` and its ${comparisons} comparison session${comparisons === 1 ? '' : 's'}`
        : '';
      toast.success(`Session duplicated (${mode === 'with_runs' ? 'with' : 'without'} runs)${withComparisons}`);
      navigate(`/sessions/${result.new_session_id}`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to duplicate session');
    }
  };

  return (
    <div className="cr-mt-4 cr-flex cr-flex-wrap cr-items-center cr-gap-3">
      <span className="cr-text-md cr-w-24 cr-shrink-0 cr-font-medium cr-text-blue-900">Duplicate</span>
      <Button
        onClick={() => duplicate('without_runs')}
        disabled={duplicateSessionMutation.isPending}
        icon={<HiDocumentDuplicate className="cr-h-5 cr-w-5" />}
        size="compact"
        normalCase
      >
        Without Runs
      </Button>
      <Button
        onClick={() => duplicate('with_runs')}
        disabled={duplicateSessionMutation.isPending}
        icon={<HiDocumentDuplicate className="cr-h-5 cr-w-5" />}
        size="compact"
        normalCase
      >
        With Runs
      </Button>
    </div>
  );
}
